'use client';

import { Mail } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { BaseModal } from '@/components/ui/base-modal';
import { Button } from '@/components/ui/button';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { logger } from '@/lib/logger';
import { useTrackEvent } from '@/lib/posthog';
import { SUPPORT_EMAIL } from '@/lib/support';

interface ContactUsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactUsModal({ isOpen, onClose }: ContactUsModalProps) {
  const { track } = useTrackEvent();

  // Fire `support_link_opened` once per transition into the open state.
  // Guarded by isOpen so re-renders while open don't double-emit.
  React.useEffect(() => {
    if (!isOpen) return;
    track(AnalyticsEvents.SUPPORT_LINK_OPENED);
  }, [isOpen, track]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch (error) {
      logger.warn('Clipboard write failed in ContactUsModal', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error("Couldn't copy — select the address above and copy it manually");
      return;
    }
    toast.success('Email address copied');
    track(AnalyticsEvents.SUPPORT_LINK_ACTION, { action: 'copy' });
  };

  const handleOpenInEmail = () => {
    track(AnalyticsEvents.SUPPORT_LINK_ACTION, { action: 'open' });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Need a hand?"
      subtitle="Email us and a real human will reply. We usually respond within a day or two."
      size="sm"
    >
      <div className="flex flex-col gap-3">
        <div
          className="flex items-stretch rounded-lg overflow-hidden"
          style={{
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--accent-20)',
          }}
        >
          <span
            className="flex-1 px-3 py-2 text-sm font-mono"
            style={{ color: 'var(--text-primary)', userSelect: 'all' }}
          >
            {SUPPORT_EMAIL}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="px-3 text-sm font-medium border-l hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            style={{ color: 'var(--text-primary)', borderColor: 'var(--border-default)' }}
          >
            Copy
          </button>
        </div>

        <Button asChild variant="primary" size="md" className="w-full">
          <a href={`mailto:${SUPPORT_EMAIL}`} onClick={handleOpenInEmail}>
            <Mail className="w-4 h-4" />
            <span>Open in email</span>
          </a>
        </Button>

        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          If the button doesn&apos;t open your email app, copy the address above and paste it into
          the mail tool you use.
        </p>
      </div>
    </BaseModal>
  );
}
